import * as THREE from 'three'

export type FacePropStyle =
  | 'nose-stud'
  | 'nose-ring'
  | 'bindi-round'
  | 'bindi-teardrop'
  | 'tikka'
  | 'cap'
  | 'beanie'
  | 'headphones'

export interface ProceduralFacePropOptions {
  style: FacePropStyle
  /** Primary colour (metal / fabric / bindi). */
  color: number | string
  /** Accent colour (stone / pendant / cushions). */
  accent: number | string
  size: number
}

export const FACE_PROP_DEFAULTS: ProceduralFacePropOptions = {
  style: 'bindi-round',
  color: 0xb01030,
  accent: 0xd4a94a,
  size: 1,
}

function metal(color: number | string): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: new THREE.Color(color), metalness: 1, roughness: 0.25, envMapIntensity: 1.1 })
}
function matte(color: number | string, roughness = 0.55): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: new THREE.Color(color), metalness: 0, roughness, envMapIntensity: 0.6 })
}
function glossy(color: number | string): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({ color: new THREE.Color(color), metalness: 0, roughness: 0.25, clearcoat: 0.8, clearcoatRoughness: 0.2, envMapIntensity: 0.7 })
}

/**
 * Procedural face props in centimetres, authored in the anchor's local frame:
 * origin at the anchor point, +X = subject's left, +Y up, +Z out of the face/head.
 * nose-*: origin at the nostril · bindi/tikka: on the skin (z≈0, bias outward)
 * cap/beanie/headphones: origin at head_top.
 */
export function createProceduralFaceProp(options: Partial<ProceduralFacePropOptions> = {}): THREE.Group {
  const o = { ...FACE_PROP_DEFAULTS, ...options }
  const s = o.size
  const g = new THREE.Group()
  g.name = `procedural-${o.style}`

  switch (o.style) {
    case 'nose-stud': {
      const stud = new THREE.Mesh(new THREE.SphereGeometry(0.18 * s, 16, 12), metal(o.accent))
      stud.name = 'stud'
      stud.position.z = 0.08 * s
      const stone = new THREE.Mesh(new THREE.OctahedronGeometry(0.13 * s, 1), glossy(o.color))
      stone.name = 'stone'
      stone.position.z = 0.24 * s
      g.add(stud, stone)
      break
    }
    case 'nose-ring': {
      // hoop through the nostril wall: plane sagittal (normal ≈ X), lower half hangs below the rim
      const hoop = new THREE.Mesh(new THREE.TorusGeometry(0.55 * s, 0.07 * s, 8, 32), metal(o.accent))
      hoop.name = 'hoop'
      // the nostril wall faces ~35° outward: angle the hoop plane with it so the arc reads from the front
      hoop.rotation.y = Math.PI / 2 - 0.62
      hoop.rotation.x = 0.25
      // hang clearly below/outside the nostril rim so the face occluder only hides the pierced part
      hoop.position.set(0.1 * s, -0.5 * s, 0.4 * s)
      g.add(hoop)
      break
    }
    case 'bindi-round': {
      const dot = new THREE.Mesh(new THREE.SphereGeometry(0.38 * s, 24, 16), glossy(o.color))
      dot.name = 'bindi'
      dot.scale.z = 0.28
      g.add(dot)
      break
    }
    case 'bindi-teardrop': {
      const drop = new THREE.Mesh(new THREE.SphereGeometry(0.32 * s, 24, 16), glossy(o.color))
      drop.scale.set(0.75, 1.25, 0.28)
      drop.name = 'drop'
      const tip = new THREE.Mesh(new THREE.ConeGeometry(0.2 * s, 0.5 * s, 16), glossy(o.color))
      tip.scale.z = 0.3
      tip.position.y = 0.5 * s
      tip.name = 'tip'
      const rim = new THREE.Mesh(new THREE.TorusGeometry(0.33 * s, 0.035 * s, 8, 24), metal(o.accent))
      rim.scale.set(0.75, 1.25, 0.5)
      rim.name = 'rim'
      g.add(drop, tip, rim)
      break
    }
    case 'tikka': {
      // chain from the hairline down the forehead slope to a pendant between the brows
      const len = 3.1 * s
      const chain = new THREE.Mesh(new THREE.CylinderGeometry(0.05 * s, 0.05 * s, len, 8), metal(o.accent))
      chain.name = 'chain'
      chain.position.y = -len / 2
      const pendant = new THREE.Mesh(new THREE.SphereGeometry(0.55 * s, 24, 16), glossy(o.color))
      pendant.scale.z = 0.35
      pendant.name = 'pendant'
      pendant.position.y = -len
      const rim = new THREE.Mesh(new THREE.TorusGeometry(0.57 * s, 0.05 * s, 8, 28), metal(o.accent))
      rim.scale.z = 0.5
      rim.position.y = -len
      rim.name = 'pendant_rim'
      const beadGeo = new THREE.SphereGeometry(0.07 * s, 8, 6)
      for (let i = 0; i < 5; i++) {
        const a = (i / 4 - 0.5) * Math.PI * 0.7
        const bead = new THREE.Mesh(beadGeo, metal(o.accent))
        bead.position.set(Math.sin(a) * 0.75 * s, -len - Math.cos(a) * 0.75 * s, 0)
        g.add(bead)
      }
      // the whole piece lies against the forehead, which slopes back: tilt slightly
      g.add(chain, pendant, rim)
      for (const child of [...g.children]) child.rotateX(0)
      const wrap = new THREE.Group()
      wrap.name = 'tikka'
      wrap.add(...g.children)
      wrap.rotation.x = -0.22
      g.add(wrap)
      break
    }
    case 'cap': {
      // deeper dome that wraps the skull down to the hairline (rim ≈ 5.4 cm below head-top)
      const dome = new THREE.Mesh(new THREE.SphereGeometry(8.4 * s, 32, 22, 0, Math.PI * 2, 0, Math.PI * 0.62), matte(o.color, 0.7))
      dome.name = 'dome'
      dome.position.y = -3.0 * s
      dome.scale.set(1, 0.8, 1.03)
      // brim: flattened partial disc pointing forward (+Z), attached at the dome's front rim
      const brim = new THREE.Mesh(new THREE.CylinderGeometry(7.2 * s, 7.2 * s, 0.35 * s, 28, 1, false, -Math.PI * 0.38, Math.PI * 0.76), matte(o.color, 0.7))
      brim.name = 'brim'
      brim.position.set(0, -5.4 * s, 3.6 * s)
      brim.rotation.x = 0.16
      const button = new THREE.Mesh(new THREE.SphereGeometry(0.5 * s, 12, 8), matte(o.color, 0.7))
      button.name = 'button'
      button.position.y = 3.8 * s
      g.add(dome, brim, button)
      break
    }
    case 'beanie': {
      const dome = new THREE.Mesh(new THREE.SphereGeometry(8.8 * s, 32, 20, 0, Math.PI * 2, 0, Math.PI * 0.58), matte(o.color, 0.85))
      dome.name = 'dome'
      dome.position.y = -4.6 * s
      dome.scale.set(1, 0.95, 1.05)
      const band = new THREE.Mesh(new THREE.TorusGeometry(8.5 * s, 0.9 * s, 12, 36), matte(o.color, 0.9))
      band.name = 'band'
      band.rotation.x = Math.PI / 2
      band.position.y = -8.6 * s
      band.scale.set(1, 1.06, 1)
      g.add(dome, band)
      break
    }
    case 'headphones': {
      // cups at the canonical ears relative to head_top (0, 11.8, 0.8): (±7.66, -11.1, -3.2)
      const mat = matte(o.color, 0.4)
      const cushion = matte(o.accent, 0.9)
      const bandR = 8.36 * s
      const band = new THREE.Mesh(new THREE.TorusGeometry(bandR, 0.55 * s, 10, 48, Math.PI * 1.26), mat)
      band.name = 'band'
      // arc centred at the top: rotate so the arc spans symmetric over the head
      band.rotation.z = Math.PI - (Math.PI * 1.26) / 2 + Math.PI / 2 - Math.PI / 2
      band.rotation.z = (Math.PI - Math.PI * 1.26) / 2
      band.position.set(0, -8.3 * s, -3.2 * s)
      for (const side of [1, -1] as const) {
        const cup = new THREE.Mesh(new THREE.CylinderGeometry(3.1 * s, 3.4 * s, 1.6 * s, 24), mat)
        cup.name = side > 0 ? 'cup_L' : 'cup_R'
        cup.rotation.z = Math.PI / 2
        cup.position.set(side * 8.0 * s, -11.1 * s, -3.2 * s)
        const pad = new THREE.Mesh(new THREE.TorusGeometry(2.6 * s, 0.7 * s, 10, 24), cushion)
        pad.name = side > 0 ? 'pad_L' : 'pad_R'
        pad.rotation.y = Math.PI / 2
        pad.position.set(side * 7.15 * s, -11.1 * s, -3.2 * s)
        g.add(cup, pad)
      }
      g.add(band)
      break
    }
  }
  return g
}
